import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useContentRun, useContentPieces, useUpdatePiece } from "../hooks/useContent";
import { useApprovePiece, useRejectPiece, useRegeneratePiece } from "../hooks/useReview";
import { usePublishRun, usePublishStatus, useReconcileBlueskyPublish, useRetryPublish } from "../hooks/usePublishing";
import { useAccountConnections, useConnectionResourceAssignments, useConnectionResources } from "../hooks/useAccountConnections";
import { useRun } from "../hooks/useRun";
import { useTaskRunBrief } from "../hooks/useTaskRunBrief";
import { ContentReview } from "../components/content/ContentReview";
import { PipelineStatus } from "../components/pipeline/PipelineStatus";
import { PublishButton } from "../components/publishing/PublishButton";
import { PublishStatus } from "../components/publishing/PublishStatus";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { OperatorBriefReport } from "../components/briefs/OperatorBriefReport";
import { ApiError } from "../lib/api";
import { workObjectDisplaySpec, type WorkObjectDisplayState } from "../lib/work-object-display";
import type { TaskRunBriefDetail } from "../../../shared/types";

function reviewDetailErrorMessage(error: unknown): { title: string; message: string } {
  if (error instanceof ApiError) {
    const reason = typeof error.details === "object" && error.details !== null && "reason" in error.details
      ? String((error.details as { reason?: unknown }).reason ?? "")
      : "";
    if (reason === "document_not_found" || reason === "document_key_not_found") {
      return {
        title: "Brief detail unavailable",
        message: "We found the run, but its stored output document could not be resolved yet.",
      };
    }
    if (reason === "brief_id_missing") {
      return {
        title: "Brief linkage missing",
        message: "This run completed without enough brief linkage to open a stored output document.",
      };
    }
    if (reason === "brief_linkage_mismatch" || reason === "document_linkage_mismatch") {
      return {
        title: "Brief linkage mismatch",
        message: "This run points to a brief or document that does not match the current run context.",
      };
    }
  }
  return {
    title: "Brief detail unavailable",
    message: "We couldn't load the brief detail for this run. The run may not have a stored brief document yet, or the backing service may be stale.",
  };
}

function reviewRouteBriefPresentation(brief: TaskRunBriefDetail): {
  objectType: "brief" | "draft" | "voice_review" | "published_handoff" | "daily_brief";
  surfaceLabel: string;
  primaryTabLabel: string;
  openOutputLabel: string;
} {
  switch (brief.document_type) {
    case "voice_review":
      return {
        objectType: "voice_review",
        surfaceLabel: "Voice Review",
        primaryTabLabel: "Review",
        openOutputLabel: "Open canonical review",
      };
    case "published":
      return {
        objectType: "published_handoff",
        surfaceLabel: "Published Handoff",
        primaryTabLabel: "Handoff",
        openOutputLabel: "Open published output",
      };
    case "daily_brief":
      return {
        objectType: "daily_brief",
        surfaceLabel: "Daily Brief",
        primaryTabLabel: "Brief",
        openOutputLabel: "Open canonical output",
      };
    case "brief":
      return {
        objectType: "brief",
        surfaceLabel: "Brief",
        primaryTabLabel: "Brief",
        openOutputLabel: "Open canonical output",
      };
    case "draft":
    default:
      return {
        objectType: "draft",
        surfaceLabel: "Draft Review",
        primaryTabLabel: "Draft",
        openOutputLabel: "Open canonical output",
      };
  }
}

export function Review() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const { data: runData, isLoading: runLoading, error: runError } = useContentRun(runId);
  // Run-type detection: a content-pipeline run resolves via useContentRun. Only fall
  // back to the task-run / brief queries when the content run did not resolve, so a
  // content run renders the draft workspace instead of the brief-detail surface.
  const shouldLoadTaskFallback = !!runId && !runLoading && !runData?.data;
  const { data: taskRunData, isLoading: taskRunLoading } = useRun(runId, shouldLoadTaskFallback);
  const taskRun = taskRunData?.data;
  const {
    data: taskRunBriefData,
    isLoading: taskRunBriefLoading,
    error: taskRunBriefError,
  } = useTaskRunBrief(runId, shouldLoadTaskFallback);
  const { data: piecesData, isLoading: piecesLoading } = useContentPieces(runData?.data ? runId : undefined);

  const approvePiece = useApprovePiece();
  const rejectPiece = useRejectPiece();
  const updatePiece = useUpdatePiece();
  const regeneratePiece = useRegeneratePiece(runId || "");
  const publishRun = usePublishRun(runId || "");
  const { data: publishStatusData } = usePublishStatus(runId);
  const retryPublish = useRetryPublish();
  const reconcileBlueskyPublish = useReconcileBlueskyPublish();
  const [linkedinConnectionId, setLinkedinConnectionId] = useState("");
  const [linkedinResourceId, setLinkedinResourceId] = useState("");
  const [blueskyConnectionId, setBlueskyConnectionId] = useState("");
  const [blueskyResourceId, setBlueskyResourceId] = useState("");
  const [reconcilingPieceIds, setReconcilingPieceIds] = useState<Set<string>>(() => new Set());
  const brandId = runData?.data?.brand_id;
  const connectionsQ = useAccountConnections(brandId);
  const connectionRows = Array.isArray(connectionsQ.data?.data) ? connectionsQ.data.data : [];
  const linkedinConnections = connectionRows.filter((connection) =>
    connection.platform === "linkedin"
    && connection.status === "connected"
    && connection.capabilities?.some((grant) => grant.capability === "linkedin.publish.write" && grant.status === "connected"),
  );
  const selectedLinkedInConnectionId = linkedinConnectionId || (linkedinConnections.length === 1 ? linkedinConnections[0]!.id : "");
  const linkedinResourcesQ = useConnectionResources(selectedLinkedInConnectionId || undefined);
  const linkedinAssignmentsQ = useConnectionResourceAssignments(selectedLinkedInConnectionId || undefined);
  const assignmentRows = Array.isArray(linkedinAssignmentsQ.data?.data) ? linkedinAssignmentsQ.data.data : [];
  const resourceRows = Array.isArray(linkedinResourcesQ.data?.data) ? linkedinResourcesQ.data.data : [];
  const now = Date.now();
  const assignedLinkedInResourceIds = new Set(assignmentRows
    .filter((assignment) => assignment.status === "active"
      && assignment.brand_id === brandId
      && Date.parse(assignment.valid_from) <= now
      && (assignment.valid_until === null || Date.parse(assignment.valid_until) > now))
    .map((assignment) => assignment.resource_id));
  const linkedinResources = resourceRows.filter((resource) =>
    resource.resource_type === "social_account" && assignedLinkedInResourceIds.has(resource.id),
  );
  const selectedLinkedInResourceId = linkedinResourceId || (linkedinResources.length === 1 ? linkedinResources[0]!.id : "");
  const selectedLinkedInConnectionIsEligible = linkedinConnections.some((connection) => connection.id === selectedLinkedInConnectionId);
  const selectedLinkedInResourceIsEligible = linkedinResources.some((resource) => resource.id === selectedLinkedInResourceId);
  const blueskyConnections = connectionRows.filter((connection) =>
    (connection.provider === "bluesky" || connection.platform === "bluesky")
    && connection.status === "connected"
    && connection.capabilities?.some((grant) => grant.capability === "bluesky.publish.write" && grant.status === "connected"),
  );
  const selectedBlueskyConnectionId = blueskyConnectionId || (blueskyConnections.length === 1 ? blueskyConnections[0]!.id : "");
  const blueskyResourcesQ = useConnectionResources(selectedBlueskyConnectionId || undefined);
  const blueskyAssignmentsQ = useConnectionResourceAssignments(selectedBlueskyConnectionId || undefined);
  const blueskyAssignmentRows = Array.isArray(blueskyAssignmentsQ.data?.data) ? blueskyAssignmentsQ.data.data : [];
  const blueskyResourceRows = Array.isArray(blueskyResourcesQ.data?.data) ? blueskyResourcesQ.data.data : [];
  const assignedBlueskyResourceIds = new Set(blueskyAssignmentRows
    .filter((assignment) => assignment.status === "active"
      && assignment.brand_id === brandId
      && Date.parse(assignment.valid_from) <= now
      && (assignment.valid_until === null || Date.parse(assignment.valid_until) > now))
    .map((assignment) => assignment.resource_id));
  const blueskyResources = blueskyResourceRows.filter((resource) =>
    resource.resource_type === "social_account" && assignedBlueskyResourceIds.has(resource.id),
  );
  const selectedBlueskyResourceId = blueskyResourceId || (blueskyResources.length === 1 ? blueskyResources[0]!.id : "");
  const selectedBlueskyConnectionIsEligible = blueskyConnections.some((connection) => connection.id === selectedBlueskyConnectionId);
  const selectedBlueskyResourceIsEligible = blueskyResources.some((resource) => resource.id === selectedBlueskyResourceId);
  useEffect(() => {
    if (linkedinResourceId && !linkedinResourcesQ.isLoading && !selectedLinkedInResourceIsEligible) {
      setLinkedinResourceId("");
    }
  }, [linkedinResourceId, linkedinResourcesQ.isLoading, selectedLinkedInResourceIsEligible]);
  useEffect(() => {
    if (blueskyResourceId && !blueskyResourcesQ.isLoading && !selectedBlueskyResourceIsEligible) {
      setBlueskyResourceId("");
    }
  }, [blueskyResourceId, blueskyResourcesQ.isLoading, selectedBlueskyResourceIsEligible]);

  if (runLoading || taskRunLoading || taskRunBriefLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!runData?.data && taskRunBriefData?.data) {
    const briefPresentation = reviewRouteBriefPresentation(taskRunBriefData.data);
    return (
      <OperatorBriefReport
        brief={taskRunBriefData.data}
        statusLabel={taskRun?.status}
        readiness={taskRun?.readiness}
        onBack={() => navigate("/home")}
        objectType={briefPresentation.objectType}
        surfaceLabel={briefPresentation.surfaceLabel}
        primaryTabLabel={briefPresentation.primaryTabLabel}
        openOutputLabel={briefPresentation.openOutputLabel}
      />
    );
  }

  if (runError || !runData?.data) {
    const detailState = taskRunBriefError
      ? reviewDetailErrorMessage(taskRunBriefError)
      : {
          title: "Run detail unavailable",
          message: "We couldn't load the content run details for this route.",
        };
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <div className="max-w-md rounded-[20px] border border-amber-200 bg-amber-50 p-5 text-center shadow-sm">
          <p className="text-sm font-medium text-amber-900">
            {detailState.title}
          </p>
          <p className="mt-2 text-sm text-amber-800">
            {detailState.message}
          </p>
          {runId && (
            <p className="mt-2 break-all text-xs text-amber-700">
              Run id: {runId}
            </p>
          )}
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button variant="secondary" size="sm" onClick={() => navigate("/home")}>
              Back to workspace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/runs")}>
              Open runs
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const run = runData.data;
  const pieces = piecesData?.data || [];
  const selectedIdea = run.selected_idea_index !== null ? run.ideas[run.selected_idea_index] : null;
  const approvedCount = pieces.filter((p) => p.status === "approved").length;

  // Pipeline status
  const isProcessing = Object.values(run.agent_status).some(
    (s) => s === "working" || s === "queued",
  );
  const hasFailed = Object.values(run.agent_status).some((s) => s === "failed");
  const reviewObjectType = "draft";
  const reviewObjectState: WorkObjectDisplayState = hasFailed ? "blocked" : "unknown";
  const reviewDisplaySpec = workObjectDisplaySpec(reviewObjectType, reviewObjectState);
  const reviewSurfaceLabel = reviewDisplaySpec.objectType === "draft" ? "Review" : reviewDisplaySpec.typeLabel;

  const handleApprove = (id: string) => {
    approvePiece.mutate(id);
  };

  const handleReject = (id: string) => {
    rejectPiece.mutate(id);
  };

  const handleEdit = (id: string, content: string) => {
    // Editing copy resets the piece to draft — an approved piece must be explicitly
    // re-approved after a manual edit (FR-5/FR-7); never let revised copy stay "approved".
    // Return the promise so the workspace closes only after the write succeeds.
    return updatePiece.mutateAsync({
      id,
      run_id: run.id,
      data: { content, character_count: content.length, status: "draft" },
    });
  };

  const handleRegenerate = (id: string) => {
    const piece = pieces.find((p) => p.id === id);
    if (piece) {
      regeneratePiece.mutate({ platform: piece.platform });
    }
  };

  const handlePublishAll = () => {
    const includesBluesky = pieces.some((piece) => piece.platform === "bluesky" && piece.status === "approved");
    publishRun.mutate({
      linkedinConnectionId: selectedLinkedInConnectionId || undefined,
      linkedinResourceId: selectedLinkedInResourceId || undefined,
      blueskyConnectionId: selectedBlueskyConnectionId || undefined,
      blueskyResourceId: selectedBlueskyResourceId || undefined,
      idempotencyKey: includesBluesky ? globalThis.crypto.randomUUID() : undefined,
    });
  };

  const handleRetryPublish = (pieceId: string) => {
    const piece = pieces.find((candidate) => candidate.id === pieceId);
    retryPublish.mutate(piece?.platform === "linkedin" ? {
      pieceId,
      linkedinConnectionId: selectedLinkedInConnectionId || undefined,
      linkedinResourceId: selectedLinkedInResourceId || undefined,
    } : piece?.platform === "bluesky" ? {
      pieceId,
      blueskyConnectionId: selectedBlueskyConnectionId || undefined,
      blueskyResourceId: selectedBlueskyResourceId || undefined,
    } : pieceId);
  };

  const handleReconcileBluesky = (pieceId: string) => {
    if (!selectedBlueskyConnectionId || !selectedBlueskyResourceId || reconcilingPieceIds.has(pieceId)) return;
    setReconcilingPieceIds((current) => new Set(current).add(pieceId));
    reconcileBlueskyPublish.mutate(
      { pieceId, connectionId: selectedBlueskyConnectionId, resourceId: selectedBlueskyResourceId },
      { onSettled: () => setReconcilingPieceIds((current) => { const next = new Set(current); next.delete(pieceId); return next; }) },
    );
  };

  // Merge pieces with publish status data when available. Guard the shape so a
  // non-array publish-status payload can never crash the review page.
  const publishedPieces = Array.isArray(publishStatusData?.data)
    ? publishStatusData.data
    : (pieces ?? []);
  const needsLinkedInTarget = pieces.some((piece) =>
    piece.platform === "linkedin" && (piece.status === "approved" || piece.publish_status === "failed"),
  );
  const linkedinTargetReady = !needsLinkedInTarget
    || (selectedLinkedInConnectionIsEligible && selectedLinkedInResourceIsEligible);
  const needsBlueskyTarget = pieces.some((piece) =>
    piece.platform === "bluesky" && (piece.status === "approved" || piece.publish_status === "failed" || piece.publish_status === "reconciliation_required"),
  );
  const blueskyTargetReady = !needsBlueskyTarget
    || (selectedBlueskyConnectionIsEligible && selectedBlueskyResourceIsEligible);
  const hasUnconfirmedPublish = publishedPieces.some((piece) => piece.publish_status === "publishing" || piece.publish_status === "reconciliation_required");

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title={selectedIdea?.headline || "Review Content"}
        description={selectedIdea?.angle || "Review generated content, resolve edits, and decide when to publish."}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Review" },
        ]}
        badges={(
          <>
            <Badge>{reviewSurfaceLabel}</Badge>
            {isProcessing ? (
              <Badge variant="warning">Processing</Badge>
            ) : hasFailed ? (
              <Badge variant="error">Failed</Badge>
            ) : (
              <Badge variant="success">Ready</Badge>
            )}
          </>
        )}
        actions={(
          <button
            type="button"
            onClick={() => navigate("/home")}
            className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
          >
            Back to workspace
          </button>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Draft decision surface
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Primary action {reviewDisplaySpec.primaryCta.label}
            </span>
          </>
        )}
      />

      <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Draft review</h2>
            <p className="mt-1 text-sm text-slate-600">
              Read the actual draft first, then approve, request edits, or publish the approved set.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            {approvedCount} approved of {pieces.length}
          </span>
        </div>

        <ContentReview
          pieces={pieces}
          onApprove={handleApprove}
          onReject={handleReject}
          onEdit={handleEdit}
          onRegenerate={handleRegenerate}
          loading={piecesLoading}
        />
      </div>

      {((isProcessing || hasFailed) || publishedPieces.some((p) => p.publish_status !== null) || publishedPieces.some((p) => p.publish_status === "published")) && (
        <details className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm" open>
          <summary className="cursor-pointer text-sm font-semibold text-slate-900">Review context</summary>
          <div className="mt-4 space-y-4">
            {(isProcessing || hasFailed) && (
              <PipelineStatus agentStatus={run.agent_status} />
            )}

            {publishedPieces.some((p) => p.publish_status !== null) && (
              <PublishStatus
                pieces={publishedPieces}
                onRetry={handleRetryPublish}
                canRetry={(piece) => piece.platform === "linkedin" ? linkedinTargetReady : piece.platform === "bluesky" ? blueskyTargetReady : true}
                onReconcile={handleReconcileBluesky}
                canReconcile={(piece) => piece.platform === "bluesky" && blueskyTargetReady && !reconcilingPieceIds.has(piece.id)}
              />
            )}

            {publishedPieces.some((p) => p.publish_status === "published") && (
              <Link
                to={`/performance/${runId}`}
                className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                  />
                </svg>
                View Performance
              </Link>
            )}
          </div>
        </details>
      )}

      {/* Footer: Publish All Approved */}
      {pieces.length > 0 && (
        <div className="sticky bottom-20 rounded-[20px] border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur sm:bottom-4">
          {needsLinkedInTarget ? <div className="mb-4 grid gap-3 sm:grid-cols-2" aria-label="LinkedIn publishing target">
            <label className="text-sm font-medium text-slate-700">LinkedIn account
              <select className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={selectedLinkedInConnectionId} onChange={(event) => { setLinkedinConnectionId(event.target.value); setLinkedinResourceId(""); }}>
                <option value="">Select an account</option>
                {linkedinConnections.map((connection) => <option key={connection.id} value={connection.id}>{connection.platform_username || connection.provider_subject || "LinkedIn account"}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">Publishing identity
              <select className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={selectedLinkedInResourceId} disabled={!selectedLinkedInConnectionId} onChange={(event) => setLinkedinResourceId(event.target.value)}>
                <option value="">Select an assigned identity</option>
                {linkedinResources.map((resource) => <option key={resource.id} value={resource.id}>{resource.display_name || resource.external_id}</option>)}
              </select>
            </label>
            {!linkedinTargetReady ? <p className="text-sm text-amber-700 sm:col-span-2">Select a LinkedIn account and an identity assigned to this brand before publishing.</p> : null}
          </div> : null}
          {needsBlueskyTarget ? <div className="mb-4 grid gap-3 sm:grid-cols-2" aria-label="Bluesky publishing target">
            <label className="text-sm font-medium text-slate-700">Bluesky account
              <select className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={selectedBlueskyConnectionId} onChange={(event) => { setBlueskyConnectionId(event.target.value); setBlueskyResourceId(""); }}>
                <option value="">Select an account</option>
                {blueskyConnections.map((connection) => <option key={connection.id} value={connection.id}>{connection.platform_username || connection.provider_subject || "Bluesky account"}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">Publishing identity
              <select className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2" value={selectedBlueskyResourceId} disabled={!selectedBlueskyConnectionId} onChange={(event) => setBlueskyResourceId(event.target.value)}>
                <option value="">Select an assigned identity</option>
                {blueskyResources.map((resource) => <option key={resource.id} value={resource.id}>{resource.display_name || resource.external_id}</option>)}
              </select>
            </label>
            {!blueskyTargetReady ? <p className="text-sm text-amber-700 sm:col-span-2">Select a Bluesky account and an identity assigned to this brand before publishing.</p> : null}
          </div> : null}
          {hasUnconfirmedPublish ? <p className="mb-3 text-sm text-amber-700">One publish outcome is unconfirmed. Check its status below; other approved posts can still continue.</p> : null}
          <div className="flex justify-end">
            <PublishButton
              runId={runId || ""}
              approvedCount={approvedCount}
              onPublish={handlePublishAll}
              isPublishing={publishRun.isPending}
              disabled={!linkedinTargetReady || !blueskyTargetReady}
            />
          </div>
        </div>
      )}
    </div>
  );
}
